import React, { useState, useEffect, useContext, useCallback, useMemo } from "react";
import { usePost } from "../API";
import { InfoDict, QuestionProps } from "../Questions/Props";
import { safeParse, useMarkdown } from "../Util";
import InterfaceContext, { SupportedLanguage } from "../Context";
import { useLocalizedStrings } from "../Localization";
import { Icon, Alert, Button, Loading } from "@aidkitorg/component-library";
import * as v0 from "@aidkitorg/types/lib/survey";
import { toMarkdownWithReplacements } from "../Hooks/ModularMarkdown";
import { ApplicationStateContext } from "../Contexts/ApplicationStateContext";
import { IconName } from "@aidkitorg/component-library/src/Icon";
import { AsyncMediaContext } from "../Contexts/AsyncMediaContext";
import { extractVariables } from "@aidkitorg/types/lib/utils/conditionalContent";
import { QuestionState } from "@aidkitorg/types/lib/convoai";
import { useApplicant } from "../Contexts/ApplicantContext";
import { AttachmentType } from "../Questions/Attachment";

export function AssistanceAnalysisContent({
  url,
  analyses,
  jobs,
  assistance,
  errors,
  uid,
  info,
  alertFooterContent,
  questionId,
  setInfoKey,
  targetField
}: {
  url: string,
  analyses: AttachmentType["analyses"],
  jobs: AttachmentType["jobs"],
  assistance: v0.Attachment["assistance"],
  errors?: AttachmentType["errors"],
  uid?: string,
  info: InfoDict,  
  alertFooterContent?: JSX.Element
  questionId?: string,
  setInfoKey: QuestionProps["setInfoKey"],
  targetField: NonNullable<QuestionProps["Target Field"]>
}) {
  const context = useContext(InterfaceContext);
  const L = useLocalizedStrings();
  const inlineAlerts = assistance?.inlineAlerts ?? [];

  const pendingJobs = Object.entries(jobs ?? {})
    .filter(([field, { jobId }]) => jobId && !analyses?.[field]);

  const allInfo = {
    ...info,
    ...analyses
  };

  const activeInlineAlertCount = inlineAlerts.reduce((count, inlineAlert) => {
    const fields = extractVariables({ content: inlineAlert.content, lang: context.lang });
    if (fields.some(f => typeof allInfo[f] === 'undefined' || allInfo[f] === null || allInfo[f]?.trim() === '')) {
      return count;
    }

    const content = getInlineAlertContent({
      lang: context.lang,
      content: inlineAlert.content,
      info: allInfo,
      uid
    });

    return content ? count + 1 : count;
  }, 0);

  const successContent = useMemo(() => {
    if (!assistance?.successMessage) return "";

    const message = assistance.successMessage[context.lang as keyof typeof assistance.successMessage] || assistance.successMessage.en;
    try {
      return toMarkdownWithReplacements({
        lang: context.lang,
        content: message,
        info: allInfo,
        uid
      });
    } catch (error) {
      console.log('Success message conditional evaluation failed, using plain message:', error instanceof Error ? error.message : String(error));
      return message;
    }
  }, [assistance?.successMessage, context.lang, allInfo, uid]);

  const successMarkdown = useMarkdown(successContent);

  const shouldShowSuccessAlert = activeInlineAlertCount === 0 && !pendingJobs.length && successContent.trim().length > 0;

  let alerts = inlineAlerts.map((inlineAlert, i) => <InlineAlert
    url={url}
    index={i}
    key={`attachmentInlineAlert:${i}`}
    inlineAlert={inlineAlert}
    info={allInfo}
    alertFooterContent={alertFooterContent}
    questionId={questionId}
    uid={uid} />
  );
  
  const errorMessages = Object.entries(errors ?? {}).filter(([, value]) => !!value);
  if (shouldShowSuccessAlert) {
    alerts = [<ul className="space-y-2">
      <Alert
        iconName="CheckCircleIcon"
        className="border border-green-500"
        variant="success">
        <div className="font-semibold">{L.ai_coach.document_signals_success_title}</div>
        {successMarkdown}
      </Alert>
    </ul>];
  }  
  
  if (!pendingJobs.length && activeInlineAlertCount === 0 && !shouldShowSuccessAlert && !errorMessages.length) {
    return null;
  }
  
  return <div className="flex flex-col gap-2">
    {assistance?.indicateAnalysisRunning ? pendingJobs.map(([field]) => <Loading key={field} />) : null}
    {alerts.map((alert, i) => <React.Fragment key={alert?.key}>{alert}</React.Fragment>)}
    {errorMessages.map(([analysisTargetField, errorMessage], i) => <AnalysisErrorMessage
      key={i}
      url={url}
      setInfoKey={setInfoKey}
      analysisTargetField={analysisTargetField.replace(/_error$/, "")}
      targetField={targetField}
      errorMessage={errorMessage} />
    )}
  </div>;
};

export function InlineAlert({
  url,
  index,
  inlineAlert,
  uid,
  info,
  questionId,
  alertFooterContent
}: {
  url: string,
  index: number,
  inlineAlert: NonNullable<NonNullable<v0.Attachment["assistance"]>["inlineAlerts"]>[number],
  uid?: string,
  info: InfoDict,
  questionId?: string,
  alertFooterContent?: JSX.Element
}) {
  const context = useContext(InterfaceContext);
  const { applicationStateChanged } = useContext(ApplicationStateContext);
  const fields = useMemo(() => extractVariables({ content: inlineAlert.content, lang: context.lang }), [inlineAlert.content, context.lang]);
  const alertContent = useMemo(() => {

    // only show content if ALL variables are present
    if (fields.some(f => typeof info[f] === 'undefined' || info[f] === null || info[f]?.trim() === '')) {
      return null;
    }

    return getInlineAlertContent({
      lang: context.lang,
      content: inlineAlert.content,
      info,
      uid
    })
  }, [context.lang, inlineAlert.content, info, uid]);
  const markdown = useMarkdown(alertContent ?? "");

  useEffect(() => {
    if (!questionId) return;

    applicationStateChanged({
      questionId,
      type: "transform",
      transform(existingQuestionState) {

        const alertMessageState = safeParse(existingQuestionState?.validity?.message as string, {});
        let validityState: NonNullable<QuestionState["validity"]>["state"] = "warning";
        alertMessageState[url] ??= {};
        if (alertContent && inlineAlert.variant !== "success") {
          alertMessageState[url][index] = {
            variant: inlineAlert.variant,
            content: alertContent
          }
        } else {
          delete alertMessageState[url][index];
          // if no alerts exist
          if (!Object.keys(alertMessageState[url]).length) {
            delete alertMessageState[url];
          }
        }

        // no alerts = valid
        if (!Object.keys(alertMessageState).length) {
          validityState = "valid"
        }

        return {
          ...existingQuestionState,
          validity: {
            state: validityState,
            message: JSON.stringify(alertMessageState)
          }
        };
      }
    });
  }, [url, applicationStateChanged, questionId, alertContent, index, inlineAlert.variant]);

  if (!alertContent) {
    return <></>;
  }

  return <Alert
    iconName={inlineAlert.iconName as IconName}
    className="[&_h4]:text-sm [&_h4]:font-medium"
    variant={inlineAlert.variant}
    title={inlineAlert.title?.[context.lang as keyof typeof inlineAlert.title] || inlineAlert.title?.en || ''}>
    {markdown}
    {alertFooterContent ?? <></>}
  </Alert>
}

function AnalysisErrorMessage({ targetField, analysisTargetField, url, setInfoKey, errorMessage }: {
  targetField: string,
  analysisTargetField?: string,
  url: string,
  setInfoKey: QuestionProps["setInfoKey"]
  errorMessage: string,
}) {
  const L = useLocalizedStrings();
  const realtimeMedia = useContext(AsyncMediaContext);
  const documentAnalysis = usePost('/ai/document/analysis');
  const [loading, setLoading] = useState(false);
  const { info } = useApplicant();

  const retry = useCallback(async () => {
    setLoading(true);
    const analysisResult = await documentAnalysis({
      targetField,
      path: url,
      info: info as Record<string, string>,
      analysisTargetFields: analysisTargetField ? [analysisTargetField] : undefined
    });
    if (!("error" in analysisResult)) {
      const updates : Record<string, string> = {};
      for (const { jobId, field } of analysisResult.jobs ?? []) {

        // clear error field
        const errorField = `${field}_error`;
        const existingErrors = safeParse(info[errorField]!, {});
        existingErrors[url] = "";
        setInfoKey(errorField, JSON.stringify(existingErrors), true, false);
        
        // reset jobs field
        const jobsField = `${field}_jobs`;
        const existingJobs = safeParse(info[jobsField]!, {});
        const oldJobId = existingJobs[url]?.jobId;
        existingJobs[url] = { jobId, field };
        setInfoKey(jobsField, JSON.stringify(existingJobs), true, false);
        
        // also remove the job from async media context state
        if (oldJobId) {
          realtimeMedia.removeJob(oldJobId);
        }
      }      
    }
    setLoading(false);
  }, [targetField, url, info, realtimeMedia.removeJob, documentAnalysis, setInfoKey, analysisTargetField]);
  return <Alert
    variant="danger">
    <div className="flex flex-col gap-2">
      <div>{errorMessage}</div>
      <Button
        className="self-start"
        loading={loading}
        icon={<Icon name="ArrowPathIcon" />}
        onClick={() => retry()}
      >{L.questions.attachment.doc_assistance_try_again}</Button>      
    </div>
  </Alert>;
}

export function getInlineAlertInfoForImage({
  inlineAlerts,
  lang,
  analyses,
  info,
  imageUrl,
  allImageUrls
}: {
  inlineAlerts?: NonNullable<v0.Attachment["assistance"]>["inlineAlerts"],
  lang: string,
  analyses: { targetField: string }[],
  info: Record<string, any>,
  imageUrl?: string,
  allImageUrls: string[]
}) {
  if (!imageUrl) return {};

  const inlineAlertTargetFields = getInlineAlertTargetFields({ inlineAlerts, lang, analyses });

  if (!inlineAlertTargetFields.length) return {};

  return inlineAlertTargetFields.reduce((acc, field) => {
    const rawValue = info[field];
    const fallback = typeof rawValue === "undefined" ? undefined : Object.fromEntries(allImageUrls.map(url => [url, rawValue]));
    const valuesByUrl = safeParse(rawValue as string, fallback);
    const valueForImage = (valuesByUrl as Record<string, string | undefined> | undefined)?.[imageUrl];

    if (typeof valueForImage !== "undefined" && valueForImage !== null) {
      acc[field] = valueForImage as string;
    }

    return acc;
  }, {} as Record<string, string>);
}

/**
 * This returns a string of markdown if the alert should be shown. Otherwise it returns null
 */
export function getInlineAlertContent(params: Parameters<typeof toMarkdownWithReplacements>[0]) {
  return toMarkdownWithReplacements(params).trim() || null;
}

export function getInlineAlertTargetFields({
  inlineAlerts,
  lang,
  analyses
}: {
  inlineAlerts?: NonNullable<v0.Attachment["assistance"]>["inlineAlerts"],
  lang: string,
  analyses: { targetField: string }[]
}) {
  const inlineAlertFields = new Set(
    inlineAlerts?.flatMap(alert => extractVariables({ content: alert.content, lang: lang as SupportedLanguage })) ?? []
  );

  analyses.forEach(({ targetField }) => {
    inlineAlertFields.delete(targetField);
    inlineAlertFields.delete(`${targetField}_job`);
    inlineAlertFields.delete(`${targetField}_result_id`);
  });

  return Array.from(inlineAlertFields);
}
